/** Bounded, format-independent density candidates for first-import camera suggestions. */
export type SubjectVector = [number, number, number];
export interface SubjectCandidate {
    lo: SubjectVector;
    hi: SubjectVector;
    center: SubjectVector;
    radius: number;
    share: number;
    minimum: number;
    seedShare: number;
    seedRadius: number;
    direction: SubjectVector;
    samples: SubjectVector[];
}
interface Cell { coordinate: SubjectVector; indices: number[]; population: number }
const offsets: SubjectVector[] = [];
for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) offsets.push([x, y, z]);
const key = (v: SubjectVector): string => v.join(',');
const shifted = (a: SubjectVector, b: SubjectVector): SubjectVector => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const quantile = (a: number[], q: number): number => a[Math.floor((a.length - 1) * q)];

/** Sampling, rather than copying/decompressing every Gaussian, bounds worker memory. */
export function sampleSubjectCenters(centers: ArrayLike<number>, limit = 32768): Float32Array {
    const n = Math.floor(centers.length / 3), step = Math.max(1, Math.ceil(n / Math.max(1, limit)));
    const result = new Float32Array(Math.min(n, Math.max(1, limit)) * 3);
    let at = 0;
    for (let i = 0; i < n; i += step) {
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        if (![x, y, z].every(Number.isFinite)) continue;
        result[at++] = x; result[at++] = y; result[at++] = z;
    }
    return result.subarray(0, at);
}

function bounds(points: SubjectVector[], indices: number[], minimum: number): SubjectCandidate {
    const selected = indices.map(i => points[i]);
    const axes = [0, 1, 2].map(k => selected.map(p => p[k]).sort((a, b) => a - b));
    const lo = axes.map(a => quantile(a, .02)) as SubjectVector;
    const hi = axes.map(a => quantile(a, .98)) as SubjectVector;
    const center = lo.map((v, k) => (v + hi[k]) / 2) as SubjectVector;
    const radius = Math.hypot(...hi.map((v, k) => (v - lo[k]) / 2));
    let mx = 0, mz = 0;
    for (const p of selected) { mx += p[0]; mz += p[2]; }
    mx /= selected.length; mz /= selected.length;
    let xx = 0, xz = 0, zz = 0;
    for (const p of selected) { xx += (p[0] - mx) ** 2; xz += (p[0] - mx) * (p[2] - mz); zz += (p[2] - mz) ** 2; }
    const angle = .5 * Math.atan2(2 * xz, xx - zz);
    let nx = -Math.sin(angle), nz = Math.cos(angle);
    if (nx * -center[0] + nz * -center[2] < 0) { nx = -nx; nz = -nz; }
    const samples: SubjectVector[] = [];
    const step = Math.max(1, Math.ceil(selected.length / 512));
    for (let i = 0; i < selected.length; i += step) samples.push(selected[i]);
    const share = selected.length / points.length;
    return { lo, hi, center, radius, share, minimum, seedShare: share, seedRadius: radius, direction: [nx, 0, nz], samples };
}

/** A voxel cluster tree, not exact DBSCAN. Intended to run off the main thread. */
export function buildSubjectCandidates(sample: ArrayLike<number>): SubjectCandidate[] {
    const points: SubjectVector[] = [];
    for (let i = 0; i + 2 < sample.length; i += 3) {
        const p: SubjectVector = [sample[i], sample[i + 1], sample[i + 2]];
        if (p.every(Number.isFinite)) points.push(p);
    }
    if (points.length < 128) return [];
    const axes = [0, 1, 2].map(k => points.map(p => p[k]).sort((a, b) => a - b));
    const width = Math.hypot(...axes.map(a => quantile(a, .75) - quantile(a, .25))) / 32;
    if (!(width > 1e-12) || !Number.isFinite(width)) return [];
    const cells = new Map<string, Cell>();
    points.forEach((point, i) => {
        const coordinate = point.map(v => Math.floor(v / width)) as SubjectVector, id = key(coordinate);
        let cell = cells.get(id);
        if (!cell) { cell = { coordinate, indices: [], population: 0 }; cells.set(id, cell); }
        cell.indices.push(i);
    });
    for (const cell of cells.values()) cell.population = offsets.reduce((sum, d) => sum + (cells.get(key(shifted(cell.coordinate, d)))?.indices.length ?? 0), 0);
    const raw: SubjectCandidate[] = [];
    for (const minimum of [12, 24, 48, 96, 192, 384]) {
        const core = new Map([...cells].filter(([, cell]) => cell.population >= minimum));
        const components: number[][] = [];
        while (core.size) {
            const [id, first] = core.entries().next().value as [string, Cell];
            core.delete(id);
            const stack = [first], indices: number[] = [];
            while (stack.length) {
                const cell = stack.pop()!;
                for (const i of cell.indices) indices.push(i);
                for (const d of offsets) {
                    const neighborId = key(shifted(cell.coordinate, d)), neighbor = core.get(neighborId);
                    if (neighbor) { core.delete(neighborId); stack.push(neighbor); }
                }
            }
            components.push(indices);
        }
        components.sort((a, b) => b.length - a.length);
        for (const indices of components.slice(0, 4)) {
            if (indices.length / points.length < .015) continue;
            const candidate = bounds(points, indices, minimum);
            if (candidate.radius > 1e-8) raw.push(candidate);
        }
    }
    const grown: SubjectCandidate[] = [];
    for (const seed of raw) {
        if (seed.share < .09) continue;
        // A compact seed may be only a face/limb. Grow within a nearby parent,
        // but do not swallow a surrounding room or a connected background.
        const parents = raw.filter(parent => parent.minimum < seed.minimum && parent.share <= seed.share * 3 && parent.radius <= seed.radius * 2.2
            && seed.samples.filter(p => p.every((v, k) => v >= parent.lo[k] - parent.radius * .02 && v <= parent.hi[k] + parent.radius * .02)).length / seed.samples.length > .90);
        parents.sort((a, b) => b.share - a.share);
        const parent = parents[0] ?? seed;
        const candidate = { ...parent, seedShare: seed.share, seedRadius: seed.radius };
        const existing = grown.find(c => c.lo.every((v, k) => v === candidate.lo[k]) && c.hi.every((v, k) => v === candidate.hi[k]));
        if (!existing) grown.push(candidate);
        else if (seed.share / seed.radius ** 2 > existing.seedShare / existing.seedRadius ** 2) Object.assign(existing, candidate);
    }
    grown.sort((a, b) => b.seedShare / b.seedRadius ** 2 - a.seedShare / a.seedRadius ** 2);
    return grown.slice(0, 4);
}
